/**
 * Today's date in the office's calendar, as a value that re-renders when it changes.
 *
 * `today()` alone is read once per render, which is wrong for the two screens that are
 * *about* today: the renewals board is «what must I call today» and the header strip says
 * «Σήμερα». A tab left open overnight went on answering yesterday's question until somebody
 * reloaded it — and in this office the browser is left open.
 *
 * Polled rather than scheduled for the one instant of midnight: a machine asleep at 00:00
 * never fires a timer set for it, and waking up is exactly when the answer is wanted. A
 * minute is fine at this cost — `setDate` with an unchanged string re-renders nothing,
 * because React bails out on an identical value.
 */

import * as React from 'react';
import { today, type IsoDate } from '@ias/shared';

export function useOfficeToday(): IsoDate {
  const [date, setDate] = React.useState(today);

  React.useEffect(() => {
    const check = () => setDate(today());
    const timer = setInterval(check, 60_000);
    // The wake-up and the tab-switch, which is when a stale board is actually looked at.
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return date;
}
